import React, { useState, useCallback } from 'react';
import './PowerOptionsSelector.css';
import { 
  getElectricOptions, 
  getNonElectricOptions, 
  getAccessOptions 
} from './productRules';
import PlatformFeatures from './PlatformFeatures';

const PowerOptionsSelector = ({ selectedModel, config, onChange }) => {
  // State to track the selected power type and options
  const [localState, setLocalState] = useState({
    powerType: config.powerType || 'electric',
    electricOption: config.electricOption || '',
    accessOption: config.accessOption || '',
    platformFeatures: config.platformFeatures || [
      {
        id: 'status-monitoring',
        label: 'Urmărire stare de funcționare',
        description: 'Permite monitorizarea stării de funcționare în timp real a eco-insulei, inclusiv raportarea automată a erorilor sau defecțiunilor.',
        enabled: true
      },
      {
        id: 'geolocation',
        label: 'Geolocație',
        description: 'Oferă informații despre locația exactă a eco-insulei, facilitând găsirea acesteia de către utilizatori și operatorii de salubritate.',
        enabled: true
      },
      {
        id: 'fill-level',
        label: 'Volum de umplere',
        description: 'Monitorizează continuu nivelul de umplere pentru fiecare fracție, optimizând rutele de colectare și prevenind supraîncărcarea.',
        enabled: true
      },
      {
        id: 'user-identification',
        label: 'Identificare utilizatori',
        description: 'Identifică utilizatorii și monitorizează frecvența aruncării și tipurile de fracții utilizate, permițând facturarea individuală și analiza comportamentului.',
        enabled: true
      },
      {
        id: 'electric-lock',
        label: 'Yală electrică cu card de acces',
        description: 'Sistem de blocare/deblocare electronică cu cititor de carduri RFID pentru control acces autorizat la eco-insulă.',
        enabled: true
      }
    ]
  });

  // Destructure local state for convenience
  const { powerType, electricOption, accessOption, platformFeatures } = localState;

  // Set de opțiuni disponibile
  const electricOptions = getElectricOptions(selectedModel);
  const nonElectricOptions = getNonElectricOptions(selectedModel);
  const accessOptions = getAccessOptions(selectedModel, electricOption);

  // Get description texts for selected options
  const getOptionDescription = (optionType, optionId) => {
    if (optionType === 'access') {
      const option = accessOptions.find(opt => opt.id === optionId);
      return option ? option.description : '';
    }
    if (optionType === 'nonElectric') {
      const option = nonElectricOptions.find(opt => opt.id === optionId);
      return option ? option.description : '';
    }
    return '';
  };

  // Update parent component with changes
  const notifyParent = useCallback((newState) => {
    onChange({
      powerType: newState.powerType,
      electricOption: newState.electricOption,
      accessOption: newState.accessOption,
      platformFeatures: newState.accessOption === 'with-it-platform' ? newState.platformFeatures : null
    });
  }, [onChange]);

  // Handle changes to power type
  const handlePowerTypeChange = (newType) => {
    let newElectricOption = '';
    let newAccessOption = '';
    
    // Reset other options when changing power type
    if (newType === 'electric') {
      // Set default electric option if available
      if (electricOptions.length > 0) {
        newElectricOption = electricOptions[0].id;
        // Set default access option for this electric option
        const availableAccessOptions = getAccessOptions(selectedModel, newElectricOption);
        if (availableAccessOptions.length > 0) {
          newAccessOption = availableAccessOptions[0].id;
        }
      }
    }
    
    const newState = {
      ...localState,
      powerType: newType,
      electricOption: newElectricOption,
      accessOption: newAccessOption
    };
    
    setLocalState(newState);
    notifyParent(newState);
  };

  // Handle changes to electric option
  const handleElectricOptionChange = (newOption) => {
    let newAccessOption = '';
    
    // Reset access option when changing electric option
    const availableAccessOptions = getAccessOptions(selectedModel, newOption);
    if (availableAccessOptions.length > 0) {
      newAccessOption = availableAccessOptions[0].id;
    }
    
    const newState = {
      ...localState,
      electricOption: newOption,
      accessOption: newAccessOption
    };
    
    setLocalState(newState);
    notifyParent(newState);
  };

  // Handle changes to access option
  const handleAccessOptionChange = (newOption) => {
    const newState = {
      ...localState,
      accessOption: newOption
    };
    
    setLocalState(newState);
    notifyParent(newState);
  };
  
  // Handle changes to platform features
  const handleFeatureChange = (featureId, enabled) => {
    const newPlatformFeatures = platformFeatures.map(feature => 
      feature.id === featureId ? { ...feature, enabled } : feature
    );
    
    const newState = {
      ...localState,
      platformFeatures: newPlatformFeatures
    };
    
    setLocalState(newState);
    notifyParent(newState);
  };

  // Get description for selected option
  const selectedOptionDescription = powerType === 'nonElectric' 
    ? getOptionDescription('nonElectric', 'mechanical')
    : getOptionDescription('access', accessOption);

  return (
    <div className="power-options-selector">
      {/* Opțiuni principale de alimentare */}
      <div className="main-options">
        <div className="option-group">
          <label className="option-item radio">
            <input 
              type="radio" 
              name="powerType" 
              value="electric" 
              checked={powerType === 'electric'} 
              onChange={() => handlePowerTypeChange('electric')}
            />
            <span className="option-text">Variantă Digitizată</span>
          </label>
          
          <label className="option-item radio">
            <input 
              type="radio" 
              name="powerType" 
              value="nonElectric" 
              checked={powerType === 'nonElectric'} 
              onChange={() => handlePowerTypeChange('nonElectric')}
            />
            <span className="option-text">Variantă Nedigitizată</span>
          </label>
        </div>
      </div>
      
      {/* Opțiuni pentru varianta electrică */}
      {powerType === 'electric' && (
        <div className="electric-options">
          <h4 className="options-subheader">Tip alimentare electrică</h4>
          <div className="option-group">
            {electricOptions.map(option => (
              <label key={option.id} className="option-item radio">
                <input 
                  type="radio" 
                  name="electricOption" 
                  value={option.id} 
                  checked={electricOption === option.id} 
                  onChange={() => handleElectricOptionChange(option.id)}
                />
                <span className="option-text">{option.name}</span>
              </label>
            ))}
          </div>
          
          <h4 className="options-subheader">Transmitere Date</h4>
          <div className="option-group">
            {accessOptions.map(option => (
              <label key={option.id} className="option-item radio">
                <input 
                  type="radio" 
                  name="accessOption" 
                  value={option.id} 
                  checked={accessOption === option.id} 
                  onChange={() => handleAccessOptionChange(option.id)}
                />
                <span className="option-text">{option.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Descriere pentru opțiunea selectată */}
      {selectedOptionDescription && (
        <div className="option-description">
          <p>{selectedOptionDescription}</p>
        </div>
      )}

      {/* Platform features for "with-it-platform" option */}
      {powerType === 'electric' && accessOption === 'with-it-platform' && (
        <PlatformFeatures 
          features={platformFeatures}
          onChange={handleFeatureChange}
        />
      )}
      
    </div>
  );
};

export default PowerOptionsSelector;